import { useEffect, useMemo, useRef } from 'react';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import __isEmpty from 'lodash/isEmpty';
import __merge from 'lodash/merge';
import styles from './graficaarc.module.scss';

ChartJS.register(ArcElement, Tooltip, Legend);

const blendWithWhite = (rgbString, alpha = 0.4) => {
    const match = rgbString.match(/rgb\s*\(\s*(\d+),\s*(\d+),\s*(\d+)\s*\)/);
    if (!match) return null;

    const r = parseInt(match[1], 10);
    const g = parseInt(match[2], 10);
    const b = parseInt(match[3], 10);

    const blendedR = Math.round((1 - alpha) * 255 + alpha * r);
    const blendedG = Math.round((1 - alpha) * 255 + alpha * g);
    const blendedB = Math.round((1 - alpha) * 255 + alpha * b);

    return `rgb(${blendedR}, ${blendedG}, ${blendedB})`;
};

const paramsDefault = {
    padding: { top: 40, bottom: 25, left: 5, right: 5 },
    showSecondaryGraph: false
};

const GraficArc = (props) => {
    const {
        dataMain = { data: [], labels: [], colors: [] },
        dataSecondary = { data: [], labels: [], colors: [] },
        optionsChart,
        paramsChart
    } = props;
    const params = useMemo(() => {
        return __merge({}, paramsDefault, paramsChart);
    }, [paramsChart]);
    const graphRef = useRef();

    const originalMainColors = useRef([]);
    const originalSecondaryColors = useRef([]);

    const resetColors = (chart) => {
        if (!chart) return;
        const mainDataset = chart.data.datasets.find((d) => d.id === 'main');
        const secondaryDataset = chart.data.datasets.find((d) => d.id === 'secondary');
        if (mainDataset) {
            mainDataset.backgroundColor = [...originalMainColors.current];
        }
        if (secondaryDataset) {
            secondaryDataset.backgroundColor = [...originalSecondaryColors.current];
        }
        chart.update({ duration: 250, easing: 'easeOutQuad' });
    };

    useEffect(() => {
        originalMainColors.current = [...dataMain.colors];
        originalSecondaryColors.current = [...dataSecondary.colors];
    }, [dataMain.colors, dataSecondary.colors]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            const chart = graphRef.current;
            if (!chart) return;
            const canvas = chart.canvas;
            if (!canvas.contains(event.target)) {
                resetColors(chart);
                chart.setActiveElements([]);
                chart.tooltip.setActiveElements([], { x: 0, y: 0 });
                chart.update({
                    duration: 200
                });
            }
        };
        document.addEventListener('click', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);
        document.addEventListener('pointerdown', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
            document.removeEventListener('pointerdown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        const chart = graphRef.current;
        if (!chart) return;
        const canvas = chart.canvas;
        const handleMouseLeave = () => {
            resetColors(chart);
        };
        canvas.addEventListener('mouseleave', handleMouseLeave);
        return () => {
            canvas.removeEventListener('mouseleave', handleMouseLeave);
        };
    }, [dataMain.colors, dataSecondary.colors]);

    const highlightSlice = (chart, datasetIndex, index, duration = 250, easing = 'easeOutCubic') => {
        const mainDataset = chart.data.datasets.find((d) => d.id === 'main');
        const secondaryDataset = chart.data.datasets.find((d) => d.id === 'secondary');
        const targetDataset = chart.data.datasets[datasetIndex];
        if (!chart || !targetDataset) return;

        if (targetDataset.id === 'main') {
            mainDataset.backgroundColor = mainDataset.data.map((_, i) =>
                i === index ? originalMainColors.current[i] : blendWithWhite(originalMainColors.current[i])
            );
            if (secondaryDataset) {
                secondaryDataset.backgroundColor = [...originalSecondaryColors.current];
            }
        } else if (targetDataset.id === 'secondary') {
            secondaryDataset.backgroundColor = secondaryDataset.data.map((_, i) =>
                i === index ? originalSecondaryColors.current[i] : blendWithWhite(originalSecondaryColors.current[i])
            );
            if (mainDataset) {
                mainDataset.backgroundColor = [...originalMainColors.current];
            }
        }
        chart.update({ duration, easing });
    };

    const mainGraphProps = {
        id: 'main',
        backgroundColor: dataMain.colors,
        hoverBackgroundColor: dataMain.colors,
        hoverOffset: dataMain.noData ? 0 : 20,
        radius: '100%',
        borderWidth: 0,
        hoverBorderWidth: 0,
        cutout: params.showSecondaryGraph ? '8%' : '40%'
    };
    const secondaryGraphProps = {
        id: 'secondary',
        backgroundColor: dataSecondary.colors,
        hoverOffset: 20,
        radius: '100%',
        cutout: '39%',
        borderWidth: 0,
        hoverBorderWidth: 0
    };
    const spacerDataset = {
        id: 'spacer',
        data: [1],
        backgroundColor: ['transparent'],
        radius: '100%',
        borderWidth: 0,
        tooltip: { enabled: false },
        hoverBackgroundColor: ['transparent'],
        noTooltip: true
    };
    const createDataset = (dataGraphic, arc) => {
        const dataset = { data: dataGraphic.dataNum, ...arc };
        dataset.dataString = dataGraphic.dataString;
        dataset.noData = dataGraphic.noData || false;
        return [dataset];
    };
    const createDualDataset = (primary, secondary, arcPrimary, arcSecondary, spacerDataset) =>
        params.showSecondaryGraph
            ? [...createDataset(primary, arcPrimary), spacerDataset, ...createDataset(secondary, arcSecondary)]
            : createDataset(primary, arcPrimary);

    const dataGraph = {
        datasets: createDualDataset(dataMain, dataSecondary, mainGraphProps, secondaryGraphProps, spacerDataset)
    };

    const doughnutPlugin = {
        afterDatasetsDraw(chart) {
            const {
                chartArea: { left, top, right, bottom },
                ctx
            } = chart;

            const chartWidth = right - left;
            const chartHeight = bottom - top;
            const graphAspectRatio = 2;
            const graphHeight = chartWidth / graphAspectRatio < chartHeight ? chartWidth / graphAspectRatio : chartHeight;
            const verticalMargins = (chartHeight - graphHeight) / 2;
            const center = (chartWidth + params.padding.left + params.padding.right) / 2;
            const sizeProps = {
                left,
                top,
                right,
                bottom,
                center,
                chartWidth,
                chartHeight,
                graphAspectRatio,
                graphHeight,
                verticalMargins
            };
            if (params.printOverlayInfo) params.printOverlayInfo(ctx, sizeProps);
        }
    };

    const options = useMemo(() => {
        const baseOptions = {
            responsive: true,
            maintainAspectRatio: false,
            rotation: -90,
            circumference: 180,
            animation: {
                duration: 0
            },
            plugins: {
                tooltip: {
                    enabled: true,
                    filter: (tooltipItem) => !tooltipItem.dataset.noData && !tooltipItem.dataset.noTooltip,
                    backgroundColor: '#21292C',
                    titleColor: '#FFF',
                    bodyColor: '#FFF',
                    borderWidth: 0,
                    padding: 10,
                    cornerRadius: 8,
                    usePointStyle: true,
                    boxWidth: 8,
                    boxHeight: 8,
                    titleFont: {
                        family: "'Bw 3cat', Helvetica, Arial, sans-serif",
                        size: 16,
                        style: 'normal',
                        weight: 'normal'
                    },
                    bodyFont: {
                        family: "'Bw 3cat', Helvetica, Arial, sans-serif",
                        size: 16,
                        style: 'normal',
                        weight: 'normal'
                    },
                    footerFont: {
                        family: "'Bw 3cat', Helvetica, Arial, sans-serif",
                        size: 12,
                        style: 'normal',
                        weight: 'bold'
                    },
                    callbacks: {
                        title: function () {
                            return '';
                        },
                        label: function (context) {
                            const { dataset, dataIndex } = context;
                            if (dataset?.noTooltip) return;
                            let labels;
                            if (dataset.id === 'main') {
                                labels = dataMain.labels;
                            } else if (dataset.id === 'secondary') {
                                labels = dataSecondary.labels;
                            }
                            if (!labels) return;
                            const label = labels[dataIndex] || 'Partit';
                            return ` ${label}`;
                        },
                        footer: function (context) {
                            if (!context?.length) return;
                            const { dataset, dataIndex } = context[0];
                            if (dataset?.noTooltip) return;
                            let dataString;
                            if (dataset.id === 'main') {
                                dataString = dataMain.dataString;
                            } else if (dataset.id === 'secondary') {
                                dataString = dataSecondary.dataString;
                            }
                            if (!dataString) return;
                            return params.printTooltip(dataString[dataIndex]);
                        }
                    }
                },
                legend: {
                    display: false
                }
            },
            layout: {
                padding: params.padding
            },
            onLeave: (event) => {
                resetColors(graphRef.current);
            },
            onClick: (event, elements) => {
                event.native?.stopPropagation?.();
                const chart = graphRef.current;
                if (!chart) return;

                if (!elements.length) {
                    resetColors(chart);
                    chart.setActiveElements([]);
                    chart.tooltip.setActiveElements([], { x: 0, y: 0 });
                    chart.update();
                    return;
                }
                const { datasetIndex, index } = elements[0];
                highlightSlice(chart, datasetIndex, index, 250, 'easeOutQuad');
            },
            onHover: (event, chartElements) => {
                const chart = graphRef.current;
                if (!chart) return;

                if (!chartElements.length) {
                    resetColors(chart);
                    return;
                }
                const { datasetIndex, index } = chartElements[0];
                highlightSlice(chart, datasetIndex, index, 150, 'easeOutCubic');
            }
        };
        return __merge({}, baseOptions, optionsChart);
    }, [optionsChart, params, dataMain, dataSecondary]);

    return (
        <div className={styles.content}>
            <div className={styles.grafic}>
                <Doughnut data={dataGraph} options={options} plugins={[doughnutPlugin]} ref={graphRef} />
            </div>
        </div>
    );
};

export default GraficArc;
